// src/context/auth.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, signOut as fbSignOut } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { auth, db } from "../api/firebase";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);       // Firebase Auth user
  const [userDoc, setUserDoc] = useState(null);  // Firestore users/{uid}
  const [claims, setClaims] = useState({});      // ID トークンのカスタムクレーム（admin / adult / paid）
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        // 権限はカスタムクレームで持つ（users/{uid}.role は本人が書けるので信用しない）。
        // クレーム付与直後はトークンが古いことがあるので強制更新で取り直す。
        try {
          const token = await firebaseUser.getIdTokenResult(true);
          setClaims(token.claims || {});
        } catch (e) {
          setClaims({});
        }
      } else {
        setClaims({});
      }

      if (firebaseUser) {
        const ref = doc(db, "users", firebaseUser.uid);
        const snap = await getDoc(ref);

        if (snap.exists()) {
          setUserDoc(snap.data());
        } else {
          // 初回ログイン時にユーザードキュメントを作成
          const newDoc = {
            displayName: firebaseUser.displayName || "",
            email: firebaseUser.email || "",
            photoURL: firebaseUser.photoURL || "",
            plan: "free",
            supportLevel: 0,
            createdAt: new Date(),
          };
          await setDoc(ref, newDoc);
          setUserDoc(newDoc);
        }
      } else {
        setUserDoc(null);
      }

      setLoading(false);
    });

    return unsub;
  }, []);

  const signOut = () => fbSignOut(auth);
  const isAdmin = claims.admin === true;
  const isAdult = claims.adult === true;
  const isPaid = claims.paid === true;

  // userDocのフィールドをローカルにもマージ（Firestore書き込み後に呼ぶ用）
  const patchUserDoc = (fields) => setUserDoc((prev) => prev ? { ...prev, ...fields } : prev);

  return (
    <AuthContext.Provider value={{ user, userDoc, claims, loading, signOut, isAdmin, isAdult, isPaid, patchUserDoc }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
